import { EyeIcon } from '@heroicons/react/24/outline';
import { Tabs } from '@/components/chariot-ui';
import React, { useCallback, useMemo } from 'react';

import { AuthenticatedRoute } from '@/app/constant';
import { Button } from '@/components/ui/button';
import { CopyToClipboard } from '@/components/CopyToClipboard';
import { OverflowText } from '@/components/OverflowText';
import { Container } from '@/components/legacy-ui';
import {
  CustomerTypeTag,
  StatusBadge,
} from '@/sections/customerManagement/components/badges/StatusBadge';
import { useCalculatedCustomerStatus } from '@/sections/customerManagement/hooks/status';
import { CustomerData } from '@/sections/customerManagement/types';
import { useAuth } from '@/state/auth';
import { useGlobalState } from '@/state/global.state';
import { cn } from '@/utils/classname';

import { CustomerMonitoringTab } from './components/CustomerMonitoringTab';
import { CustomerSettingsTab } from './components/CustomerSettingsTab';
import { CustomerSharingTab } from './components/CustomerSharingTab';
import { CustomerSubscriptionTab } from './components/CustomerSubscriptionTab';
import { CustomerUsersTab } from './components/CustomerUsersTab';

interface CustomerDrawerProps {
  customer: CustomerData;
}

export const CustomerDrawer: React.FC<CustomerDrawerProps> = ({ customer }) => {
  const { drawerState } = useGlobalState();
  const { startImpersonation, isPraetorianUser } = useAuth();

  // Calculate status dynamically to ensure consistency with dashboard
  const { status: calculatedStatus } = useCalculatedCustomerStatus(
    customer.email,
    customer.type
  );

  // Use calculated status or fallback to customer status
  const displayStatus = (calculatedStatus ||
    customer.status) as import('@/sections/customerManagement/types').Status;

  const tabs = useMemo(() => {
    const allTabs = [
      { value: 'monitoring', label: 'Monitoring' },
      { value: 'settings', label: 'Settings' },
      { value: 'users', label: 'Users' },
      { value: 'notes', label: 'Shared Files' },
      { value: 'subscription', label: 'Subscription' },
    ];

    const praetorianOnlyTabs = new Set(['users', 'subscription']);

    return isPraetorianUser
      ? allTabs
      : allTabs.filter(tab => !praetorianOnlyTabs.has(tab.value));
  }, [isPraetorianUser]);

  const currentTab = drawerState.customerDrawerTab || 'monitoring';

  // Context-aware navigation based on current tab
  const handleViewAccount = useCallback(() => {
    // Close the drawer first
    drawerState.handleOpenCustomerDrawer(undefined);

    // Map drawer tabs to their corresponding routes
    let route: string;
    switch (currentTab) {
      case 'monitoring':
        route = AuthenticatedRoute.INSIGHTS; // Dashboard/monitoring page
        break;
      case 'settings':
        route = AuthenticatedRoute.SETTINGS; // Settings page
        break;
      case 'users':
        route = AuthenticatedRoute.SETTINGS_USER_MANAGEMENT; // User Management page
        break;
      case 'notes':
        route = AuthenticatedRoute.FILES; // Shared documents section
        break;
      case 'subscription':
        route = AuthenticatedRoute.SETTINGS_SUBSCRIPTION; // Direct subscription route
        break;
      default:
        route = AuthenticatedRoute.INSIGHTS;
    }

    // Start impersonation with the specific route
    startImpersonation(customer.email, route);
  }, [currentTab, customer.email, startImpersonation, drawerState]);

  return (
    <div key={customer.email} className="flex min-w-[900px] flex-col gap-6 p-6">
      <div className="flex w-full items-center justify-between gap-4">
        <div className="flex max-w-full flex-col gap-1 overflow-hidden">
          <div className="flex gap-2">
            <CopyToClipboard
              alwaysShowCopyIcon
              className="gap-2 overflow-hidden [&_svg]:ml-0"
            >
              <OverflowText
                className="text-2xl font-bold text-nowrap"
                text={customer.displayName || ''}
              />
            </CopyToClipboard>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={displayStatus} />
          {isPraetorianUser && <CustomerTypeTag type={customer.type} />}
        </div>
      </div>

      <div className="bg-chariot-stroke h-px w-full shrink-0"></div>

      <div className={cn('flex items-center gap-3')}>
        <Button
          onClick={handleViewAccount}
          variant="outline"
          size="sm"
          className="rounded-full"
        >
          <EyeIcon className="size-5" />
          View Account
        </Button>
      </div>

      <div className="flex flex-1 flex-col overflow-hidden">
        <Tabs
          tabs={tabs}
          selected={tabs.find(tab => tab.value === currentTab) || tabs[0]}
          onSelect={tab => drawerState.setCustomerDrawerTab(tab.value)}
          variant="primary"
          className="flex-1"
        />
      </div>

      <Container
        variant="emphasized"
        className={cn('size-full overflow-auto p-6')}
        tabIndex={-1}
        id={currentTab}
      >
        {(currentTab === 'monitoring' || currentTab === '') && (
          <CustomerMonitoringTab customer={customer} />
        )}
        {currentTab === 'subscription' && isPraetorianUser && (
          <CustomerSubscriptionTab customer={customer} />
        )}
        {currentTab === 'settings' && (
          <CustomerSettingsTab customer={customer} />
        )}
        {currentTab === 'users' && <CustomerUsersTab customer={customer} />}
        {currentTab === 'notes' && <CustomerSharingTab customer={customer} />}
      </Container>
    </div>
  );
};
